import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type { CountryPageData } from '../models/marketing.models';

export type CountryStats = {
  avgFees?: string;
  visaSuccessRate?: string;
  popularCourses?: string[];
};

export type CountrySeo = {
  title: string;
  description: string;
  keywords: string[];
  ogImageId?: string;
};

export type CountryAnnouncement = { pill?: string; text?: string; ctaLabel?: string; ctaHref?: string };

export type CountryPageStat = { value: string; label: string; icon?: string };

export type CountryWhyCard = { title: string; description: string; imageMediaAssetId?: string; imagePath?: string };

export type CountryCostBlock = { title: string; body: string; icon?: string };

export type CountryScholarship = { name: string; provider?: string; summary?: string; ctaUrl?: string };

export type CountryTimelineStep = { title: string; description: string };

export type CountryFaqItem = { question: string; answer: string };

export type CountryConsultationCta = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  bullets?: string[];
  formHeading?: string;
  legalLine?: string;
};

export type CountryDto = {
  _id: string;
  slug: string;
  name: string;
  shortDescription?: string;
  heroImageId?: string;
  flagIcon?: string;
  stats?: CountryStats;
  highlights?: string[];
  seo?: CountrySeo;
  status?: 'DRAFT' | 'PUBLISHED';
  announcement?: CountryAnnouncement;
  heroEyebrow?: string;
  heroTitle?: string;
  heroLede?: string;
  heroPrimaryCtaText?: string;
  heroPrimaryCtaAnchor?: string;
  heroSecondaryCtaText?: string;
  heroSecondaryCtaAnchor?: string;
  heroMediaAssetId?: string;
  pageStats?: CountryPageStat[];
  whyEyebrow?: string;
  whyTitle?: string;
  whySubtitle?: string;
  whyCards?: CountryWhyCard[];
  costsTitle?: string;
  costsIntro?: string;
  costBlocks?: CountryCostBlock[];
  blockedAccountBadge?: string;
  blockedAccountTitle?: string;
  blockedAccountAmount?: string;
  blockedAccountAmountSuffix?: string;
  blockedAccountDescription?: string;
  blockedAccountBullets?: string[];
  scholarships?: CountryScholarship[];
  visaTitle?: string;
  visaSubtitle?: string;
  visaSteps?: CountryTimelineStep[];
  faqItems?: CountryFaqItem[];
  consultationCta?: CountryConsultationCta;
};

export type CreateCountryPayload = {
  slug: string;
  name: string;
  shortDescription?: string;
  heroImageId?: string;
  flagIcon?: string;
  stats?: CountryStats;
  highlights?: string[];
  seo: CountrySeo;
  status?: 'DRAFT' | 'PUBLISHED';
  announcement?: CountryAnnouncement;
  heroEyebrow?: string;
  heroTitle?: string;
  heroLede?: string;
  heroPrimaryCtaText?: string;
  heroPrimaryCtaAnchor?: string;
  heroSecondaryCtaText?: string;
  heroSecondaryCtaAnchor?: string;
  heroMediaAssetId?: string;
  pageStats?: CountryPageStat[];
  whyEyebrow?: string;
  whyTitle?: string;
  whySubtitle?: string;
  whyCards?: CountryWhyCard[];
  costsTitle?: string;
  costsIntro?: string;
  costBlocks?: CountryCostBlock[];
  blockedAccountBadge?: string;
  blockedAccountTitle?: string;
  blockedAccountAmount?: string;
  blockedAccountAmountSuffix?: string;
  blockedAccountDescription?: string;
  blockedAccountBullets?: string[];
  scholarships?: CountryScholarship[];
  visaTitle?: string;
  visaSubtitle?: string;
  visaSteps?: CountryTimelineStep[];
  faqItems?: CountryFaqItem[];
  consultationCta?: CountryConsultationCta;
};

@Injectable({ providedIn: 'root' })
export class CountriesService {
  constructor(private readonly api: ApiService) {}

  listPlatformCountries() {
    return this.api.get<ApiEnvelope<CountryDto[]>>('platform/countries').pipe(map((r) => r.data));
  }

  getPlatformCountry(id: string) {
    return this.api.get<ApiEnvelope<CountryDto>>(`platform/countries/${id}`).pipe(map((r) => r.data));
  }

  listPublicCountries() {
    return this.api.get<ApiEnvelope<CountryDto[]>>('public/countries').pipe(map((r) => r.data));
  }

  getPublicCountry(slug: string) {
    return this.api.get<ApiEnvelope<CountryDto>>(`public/countries/${slug}`).pipe(map((r) => r.data));
  }

  getPublicCountryPage(slug: string) {
    return this.api
      .get<ApiEnvelope<CountryPageData>>(`public/marketing/countries/${encodeURIComponent(slug)}`)
      .pipe(map((r) => r.data));
  }

  create(payload: CreateCountryPayload) {
    return this.api.post<ApiEnvelope<CountryDto>>('platform/countries', payload).pipe(map((r) => r.data));
  }

  update(id: string, payload: Partial<CreateCountryPayload>) {
    return this.api.patch<ApiEnvelope<CountryDto>>(`platform/countries/${id}`, payload).pipe(map((r) => r.data));
  }

  remove(id: string) {
    return this.api.delete<ApiEnvelope<{ deleted: boolean }>>(`platform/countries/${id}`).pipe(map((r) => r.data));
  }
}
